import { useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import type { LegacyReviewForm } from "../../../utils/legacyReviewForm";
import {
  reviewedLegacyPatientCandidates,
  type LegacyPatientCandidate,
} from "../../../utils/legacyPatientMapping";
import LegacyPatientCandidateList from "./LegacyPatientCandidateList";

interface Props {
  form: LegacyReviewForm;
  candidates: LegacyPatientCandidate[];
  loading: boolean;
  error?: Error | null;
  onRefresh: () => void;
  onSelect: (patientId: string) => void;
}

export default function LegacyNewPatientFields({
  form,
  candidates,
  loading,
  error,
  onRefresh,
  onSelect,
}: Props) {
  const acknowledged = useMemo(
    () =>
      reviewedLegacyPatientCandidates(
        form.values.patientReviewedCandidateIds,
        candidates
      ),
    [candidates, form.values.patientReviewedCandidateIds]
  );
  const handleIdentityText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const field = event.currentTarget.dataset.identityField;
      if (
        field === "firstName" ||
        field === "lastName" ||
        field === "medicalRecordNumber"
      ) {
        form.setFieldValue(field, event.currentTarget.value);
        form.setFieldValue("patientReviewedCandidateIds", undefined);
      }
    },
    [form]
  );
  const handleDateOfBirth = useCallback(
    (value: string | null) => {
      form.setFieldValue("dateOfBirth", value || "");
      form.setFieldValue("patientReviewedCandidateIds", undefined);
    },
    [form]
  );
  const handleSex = useCallback(
    (value: string) => {
      if (value === "M" || value === "F" || value === "Other") {
        form.setFieldValue("sex", value);
        form.setFieldValue("patientReviewedCandidateIds", undefined);
      }
    },
    [form]
  );
  const handleAcknowledgment = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue(
        "patientReviewedCandidateIds",
        event.currentTarget.checked
          ? candidates.map((patient) => patient.id)
          : undefined
      );
    },
    [candidates, form]
  );
  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label="First name"
          withAsterisk
          aria-required
          autoComplete="off"
          data-identity-field="firstName"
          {...form.getInputProps("firstName")}
          onChange={handleIdentityText}
        />
        <TextInput
          label="Last name"
          withAsterisk
          aria-required
          autoComplete="off"
          data-identity-field="lastName"
          {...form.getInputProps("lastName")}
          onChange={handleIdentityText}
        />
        <DateInput
          label="Date of birth"
          withAsterisk
          aria-required
          valueFormat="MM/DD/YYYY"
          clearable
          {...form.getInputProps("dateOfBirth")}
          onChange={handleDateOfBirth}
        />
        <Radio.Group
          label="Sex"
          withAsterisk
          aria-required
          {...form.getInputProps("sex")}
          onChange={handleSex}
        >
          <Stack gap="xs">
            <Radio value="F" label="Female" />
            <Radio value="M" label="Male" />
            <Radio value="Other" label="Other" />
          </Stack>
        </Radio.Group>
        <TextInput
          label="Medical record number"
          data-identity-field="medicalRecordNumber"
          {...form.getInputProps("medicalRecordNumber")}
          onChange={handleIdentityText}
        />
      </SimpleGrid>
      {form.values.patientAction === "create" && (
        <Stack gap="sm">
          {loading && (
            <Text size="sm" role="status">
              Checking existing patients…
            </Text>
          )}
          {error && (
            <Alert color="red" title="Patient suggestions unavailable">
              {error.message}
            </Alert>
          )}
          {candidates.length > 0 && (
            <>
              <Alert color="orange" title="Existing patients need review">
                These patients were suggested from the source documents or the
                identity entered above. Compare their identities and case
                histories before creating a new patient.
              </Alert>
              <LegacyPatientCandidateList
                candidates={candidates}
                onSelect={onSelect}
              />
              <Checkbox
                label="I reviewed these existing patients and need a new patient"
                {...form.getInputProps("patientReviewedCandidateIds")}
                checked={acknowledged}
                onChange={handleAcknowledgment}
                disabled={loading || Boolean(error)}
              />
            </>
          )}
          {!loading && !error && !candidates.length && (
            <Text size="sm" c="dimmed">
              No existing patients were suggested for this source and entered
              identity.
            </Text>
          )}
          <Button variant="subtle" onClick={onRefresh} loading={loading}>
            Refresh patient suggestions
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
